/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	ArticleComponentsSection,
	CosmosComponentContextProps,
	getHero,
	heroOwnsHeading,
	getSections,
	SectionTypename,
	useCosmosComponentContext,
	useScrollClientHash,
} from "@cosmos/components";
import { PageBlogDetailFragment } from "@cosmos/hygraph-schema";
import { useAuth } from "@cosmos/next-hooks";

import { css } from "@panda/css";

import { TemplateProviders } from "../templates/TemplateProviders";

interface CosmosPageProps {
	pageData?: PageBlogDetailFragment;
	componentContext?: CosmosComponentContextProps;
}

const sectionExtraProps = (__typename: SectionTypename): any => {
	return __typename === "CosmosSectionContentLargeImage" ? { enableAnimate: false } : {};
};

export const CosmosPageBlogDetail = ({ pageData, componentContext }: CosmosPageProps): React.JSX.Element => {
	const contextFromProvider = useCosmosComponentContext();
	const ctx = componentContext ?? contextFromProvider;
	useAuth(!ctx.enableAuth);
	useScrollClientHash();

	// Only a hero that renders an h1 owns it; a decorative hero (e.g. a glow with no title)
	// must not suppress the first section's promotion — gate on heroOwnsHeading, not presence.
	const hero = getHero(pageData?.hero, pageData?.blogConfig);

	return (
		<TemplateProviders componentContext={componentContext}>
			{hero}
			<div className={css({ paddingBlockStart: "eds.dimension.32", paddingBlockEnd: "eds.dimension.64" })}>
				<ArticleComponentsSection articleComponents={pageData?.articleComponents} nAnimatedAsset={2} />
				{pageData?.articleComponents.length && pageData?.refSections?.length ? (
					<div className={css({ paddingBlock: "eds.dimension.32" })} />
				) : (
					""
				)}
				{getSections(pageData?.refSections, sectionExtraProps, heroOwnsHeading(pageData?.hero))}
			</div>
		</TemplateProviders>
	);
};
